/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Row, Col, Button, Select, Spin, Typography, Divider, Space } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const DEFAULT_NAME = 'PQT Planning';

const AddPQTReportModal = (props) => {
  const {
    visible,
    onCancel,
    onSave,
    pqtElements,
    existingReport,
    loading,
    isReportFetching,
    isWriteAccess,
    onCreatePqtElement,
  } = props;

  const [form] = Form.useForm();
  const [rows, setRows] = useState([{ elementId: undefined, bidDays: 0 }]);
  const [addNewElementName, setAddNewElementName] = useState('');
  const [addingElement, setAddingElement] = useState(false);

  const sortedElements = useMemo(() => {
    const list = Array.isArray(pqtElements) ? [...pqtElements] : [];
    return list.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' }));
  }, [pqtElements]);

  useEffect(() => {
    if (visible && existingReport) {
      const report = existingReport;
      const hasReport = report && (
        report.id != null || report.projectPqtReportId != null
        || (report.elements?.length || report.reportElements?.length)
      );
      if (hasReport) {
        form.setFieldsValue({
          description: report.description ?? '',
        });
        const elements = report.elements ?? report.reportElements ?? [];
        const items = Array.isArray(elements) ? elements : [];
        if (items.length > 0) {
          setRows(
            items.map((el) => {
              const elemId = el.pqtElementId ?? el.pqtElement?.id ?? el.id;
              const bidDays = el.bidDays ?? el.bidNoOfDays ?? 0;
              return {
                elementId: elemId,
                bidDays: Number.isFinite(bidDays) ? bidDays : 0,
              };
            })
          );
        } else {
          setRows([{ elementId: undefined, bidDays: 0 }]);
        }
      } else {
        form.resetFields();
        setRows([{ elementId: undefined, bidDays: 0 }]);
      }
    } else if (visible && !existingReport) {
      form.resetFields();
      setRows([{ elementId: undefined, bidDays: 0 }]);
    }
  }, [visible, existingReport, form]);

  const addRow = () => {
    setRows((prev) => [...prev, { elementId: undefined, bidDays: 0 }]);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddNewElement = async () => {
    const name = (addNewElementName || '').trim();
    if (!name) return;
    setAddingElement(true);
    try {
      const maxOrder = sortedElements.length > 0
        ? Math.max(...sortedElements.map((e) => e.sortOrder ?? 0), 0) + 1
        : 1;
      if (onCreatePqtElement) {
        await onCreatePqtElement({ name, sortOrder: maxOrder });
      }
      setAddNewElementName('');
    } finally {
      setAddingElement(false);
    }
  };

  const clearNewElementInput = () => {
    setAddNewElementName('');
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const { description } = values;
      const elements = rows
        .filter((r) => r.elementId != null && r.elementId !== '')
        .map((r) => ({
          id: typeof r.elementId === 'number' ? r.elementId : parseInt(r.elementId, 10),
          bidDays: typeof r.bidDays === 'number' ? r.bidDays : parseInt(r.bidDays, 10) || 0,
        }));
      const name = existingReport?.name ?? DEFAULT_NAME;
      onSave({
        name,
        description: (description || '').trim(),
        elements,
        reportId: existingReport && (existingReport.id ?? existingReport.projectPqtReportId) ? (existingReport.id ?? existingReport.projectPqtReportId) : null,
      });
    });
  };

  const modalFooter = [
    <Button key="cancel" onClick={onCancel}>
      Cancel
    </Button>,
    <Button key="submit" type="primary" onClick={handleOk} loading={loading}>
      {existingReport && (existingReport.id ?? existingReport.projectPqtReportId) ? 'Update' : 'Add'}
    </Button>,
  ];

  return (
    <Modal
      width="45vw"
      className="pipeline-modal"
      title="PQT Planning"
      centered
      visible={visible}
      maskClosable={false}
      onCancel={onCancel}
      footer={modalFooter}
    >
      {isReportFetching ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading...</div>
        </div>
      ) : (
        <Form form={form} layout="vertical" autoComplete="off" initialValues={{ description: '' }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ required: true, message: 'Description is required' }]}
              >
                <TextArea
                  placeholder="Enter description"
                  size="large"
                  rows={3}
                  style={{ resize: 'none' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
            <Col lg={14}>
              <strong style={{ marginBottom: 8 }}>Elements</strong>
            </Col>
            <Col lg={6}>
              <strong>Bid No of Days</strong>
            </Col>
            <Col lg={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addRow}
                disabled={!!isWriteAccess}
              >
                Add row
              </Button>
            </Col>
          </Row>

          {rows.map((row, index) => (
            <Row gutter={16} key={index} align="middle" style={{ marginBottom: 12 }}>
              <Col lg={14} style={{ display: 'flex', alignItems: 'center' }}>
                <Select
                  className="client-select"
                  placeholder="Select element"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  value={row.elementId}
                  onChange={(value) => handleRowChange(index, 'elementId', value)}
                  size="large"
                  style={{ width: '100%' }}
                  disabled={!!isWriteAccess}
                  dropdownRender={
                    onCreatePqtElement && !isWriteAccess
                      ? (menu) => (
                          <>
                            {menu}
                            <Divider style={{ margin: '8px 0', borderTop: '1px solid lightgrey' }} />
                            <Space align="center" style={{ padding: '0 8px 4px' }}>
                              <Input
                                placeholder="Add element"
                                value={addNewElementName}
                                onChange={(e) => setAddNewElementName(e.target.value)}
                                onPressEnter={(e) => {
                                  e.preventDefault();
                                  handleAddNewElement();
                                }}
                                size="small"
                              />
                              <Typography.Link
                                onClick={handleAddNewElement}
                                style={{ whiteSpace: 'nowrap', color: 'green' }}
                                disabled={!(addNewElementName || '').trim() || addingElement}
                              >
                                <CheckOutlined />
                              </Typography.Link>
                              <Typography.Link
                                onClick={clearNewElementInput}
                                style={{ whiteSpace: 'nowrap', color: 'red' }}
                              >
                                <CloseOutlined />
                              </Typography.Link>
                            </Space>
                          </>
                        )
                      : undefined
                  }
                >
                  {sortedElements.map((el) => (
                    <Option key={el.id} value={el.id}>
                      {el.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col lg={6} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <Input
                    type="number"
                    min={0}
                    placeholder="0"
                    value={row.bidDays}
                    onChange={(e) =>
                      handleRowChange(index, 'bidDays', parseInt(e.target.value, 10) || 0)
                    }
                    size="large"
                    disabled={!!isWriteAccess}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
              </Col>
              <Col lg={4} style={{ display: 'flex', alignItems: 'center' }}>
                {rows.length > 1 && !isWriteAccess && (
                  <Button type="link" danger onClick={() => removeRow(index)}>
                    Remove
                  </Button>
                )}
              </Col>
            </Row>
          ))}
        </Form>
      )}
    </Modal>
  );
};

export default AddPQTReportModal;
